import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Table,
  Button,
  Badge,
  Form,
  Modal,
  InputGroup,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";
import api from "../../../services/api";
import IsoDatePicker from "../../../components/IsoDatePicker";
import { formatDateDMY } from "../../../utils/dateFormat";

export default function PriceListsManager() {
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/price-lists");
      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [
        r.id,
        r.tripTitle,
        r.vehicleType,
        `${r.durationNights || 0}N ${r.durationDays || 0}D`,
        r.currencyCode,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [rows, query]);

  const upsert = async (payload) => {
    if (payload.id) {
      const { data } = await api.put(
        `/api/admin/price-lists/${payload.id}`,
        payload
      );
      setRows((prev) => prev.map((r) => (r.id === data.id ? data : r)));
    } else {
      const { data } = await api.post(`/api/admin/price-lists`, payload);
      setRows((prev) => [data, ...prev]);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this price?")) return;
    await api.delete(`/api/admin/price-lists/${id}`);
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <>
      <Card className="shadow-sm mb-3">
        <Card.Header className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
          <div className="fw-semibold">Manage Price Lists</div>
          <div className="d-flex gap-2">
            <InputGroup size="sm">
              <InputGroup.Text>Search</InputGroup.Text>
              <Form.Control
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="trip, vehicle, duration, currency"
              />
            </InputGroup>
            <Button
              size="sm"
              onClick={() => {
                setEditing(null);
                setShow(true);
              }}
            >
              + New Price
            </Button>
          </div>
        </Card.Header>

        {loading ? (
          <div className="py-5 text-center">
            <Spinner /> <div className="small text-muted mt-2">Loading…</div>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle" size="sm">
              <thead className="table-light">
                <tr><th style={{ width: 44 }}>#</th>
                  <th>ID</th>
                  <th>Trip</th>
                  <th>Duration</th>
                  <th>Vehicle</th>
                  <th className="text-end">Price</th>
                  <th className="text-end">Original</th>
                  <th>Currency</th>
                  <th>Valid</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, index) => (
                  <tr key={r.id}><td className="small fw-semibold">{index + 1}</td>
                    <td className="text-nowrap">#{r.id}</td>
                    <td className="text-truncate" style={{ maxWidth: 220 }}>
                      {r.tripTitle || r.tripName || `Trip #${r.tripId}`}
                    </td>
                    <td>{`${r.durationNights || 0}N ${
                      r.durationDays || 0
                    }D`}</td>
                    <td>{r.vehicleType}</td>
                    <td className="text-end">
                      ₹{Number(r.price || 0).toLocaleString()}
                    </td>
                    <td className="text-end">
                      {r.originalPrice != null && r.originalPrice > 0
                        ? `₹${Number(r.originalPrice).toLocaleString()}`
                        : "-"}
                    </td>
                    <td>{r.currencyCode || "INR"}</td>
                    <td className="text-nowrap">
                      {formatDateDMY(r.validFrom)} →{" "}
                      {formatDateDMY(r.validTo)}
                    </td>
                    <td>
                      <Badge bg={r.isActive ? "success" : "secondary"}>
                        {r.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="me-2"
                        onClick={() => {
                          setEditing(r);
                          setShow(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => onDelete(r.id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan={11} className="text-center py-4 text-muted">
                      No price lists found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      <PriceListModal
        show={show}
        onHide={() => setShow(false)}
        initial={editing}
        onSave={async (payload) => {
          await upsert(payload);
          setShow(false);
          setEditing(null);
        }}
      />
    </>
  );
}

function PriceListModal({ show, onHide, initial, onSave }) {
  const empty = {
    id: undefined,
    tripId: undefined,
    tripTitle: "",
    durationNights: 0,
    durationDays: 0,
    vehicleType: "Sedan(4 Seater) - 2 People",
    price: 0,
    originalPrice: "",
    currencyCode: "INR",
    validFrom: "",
    validTo: "",
    isActive: true,
  };

  const [form, setForm] = useState(initial ? normalize(initial) : empty);
  const [trips, setTrips] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(true);
  const [tripQuery, setTripQuery] = useState("");

  function normalize(v) {
    if (!v) return { ...empty };
    return {
      ...empty,
      ...v,
      durationNights: Number(v.durationNights ?? 0),
      durationDays: Number(v.durationDays ?? 0),
      price: Number(v.price ?? 0),
      originalPrice:
        v.originalPrice != null && v.originalPrice !== ""
          ? String(v.originalPrice)
          : "",
      validFrom: v.validFrom ? String(v.validFrom).slice(0, 10) : "",
      validTo: v.validTo ? String(v.validTo).slice(0, 10) : "",
      currencyCode: v.currencyCode || "INR",
      vehicleType: v.vehicleType || "Sedan(4 Seater) - 2 People",
    };
  }

  useEffect(() => {
    setForm(initial ? normalize(initial) : empty);
  }, [initial, show]);

  useEffect(() => {
    if (!show) return;
    // On Add: load all trips; On Edit we also show the list to allow reassignment if needed
    (async () => {
      setLoadingTrips(true);
      try {
        const { data } = await api.get("/api/admin/trips");
        setTrips(Array.isArray(data) ? data : []);
      } finally {
        setLoadingTrips(false);
      }
    })();
  }, [show]);

  const filteredTrips = useMemo(() => {
    const q = tripQuery.trim().toLowerCase();
    if (!q) return trips;
    return trips.filter((t) =>
      [t.id, t.title, t.path, t.categoryName, t.availability]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [trips, tripQuery]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.tripId) {
      alert("Please select a trip");
      return;
    }
    if (!form.durationDays || !form.durationNights) {
      alert("Please enter duration (nights/days)");
      return;
    }
    if (!form.price || Number(form.price) <= 0) {
      alert("Please enter a valid price");
      return;
    }
    const payload = {
      ...form,
      originalPrice:
        form.originalPrice === "" ? null : Number(form.originalPrice),
    };
    await onSave(payload);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Form onSubmit={onSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{form.id ? "Edit Price" : "Add Price"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={12}>
              <Form.Label className="fw-semibold">Select Trip</Form.Label>
              <InputGroup className="mb-2">
                <InputGroup.Text>Search</InputGroup.Text>
                <Form.Control
                  value={tripQuery}
                  onChange={(e) => setTripQuery(e.target.value)}
                  placeholder="Search trip by title, path, category"
                />
              </InputGroup>
              <div
                className="border rounded"
                style={{ maxHeight: 220, overflowY: "auto" }}
              >
                {loadingTrips ? (
                  <div className="text-center py-3">
                    <Spinner />
                  </div>
                ) : (
                  <Table hover size="sm" className="mb-0 align-middle">
                    <thead className="table-light">
                      <tr><th style={{ width: 44 }}>#</th>
                        <th style={{ width: 36 }}></th>
                        <th>ID</th>
                        <th>Title</th>
                        <th>Category</th>
                        <th>Path</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTrips.map((t, index) => (
                        <tr
                          key={t.id}
                          onClick={() => set("tripId", t.id)}
                          style={{ cursor: "pointer" }}
                        ><td className="small fw-semibold">{index + 1}</td>
                          <td>
                            <Form.Check
                              type="radio"
                              name="tripId"
                              checked={form.tripId === t.id}
                              onChange={() => set("tripId", t.id)}
                            />
                          </td>
                          <td className="text-nowrap">#{t.id}</td>
                          <td
                            className="text-truncate"
                            style={{ maxWidth: 240 }}
                          >
                            {t.title}
                          </td>
                          <td>{t.categoryName || "-"}</td>
                          <td>
                            <small className="text-muted">{t.path}</small>
                          </td>
                        </tr>
                      ))}
                      {!filteredTrips.length && (
                        <tr>
                          <td
                            colSpan={6}
                            className="text-center text-muted py-3"
                          >
                            No trips found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}
              </div>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Duration (Nights)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={form.durationNights}
                  onChange={(e) =>
                    set("durationNights", Number(e.target.value))
                  }
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Duration (Days)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={form.durationDays}
                  onChange={(e) => set("durationDays", Number(e.target.value))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Vehicle Type</Form.Label>
                <Form.Select
                  value={form.vehicleType}
                  onChange={(e) => set("vehicleType", e.target.value)}
                >
                  {[
                    "Sedan(4 Seater) - 2 People",
                    "Sedan(4 Seater) - 3 People",
                    "Sedan(4 Seater) - 4 People",
                    "Ertiga(6 Seater) - 5 People",
                    "Ertiga(6 Seater) - 6 People",
                    "Innova(7 Seater) - 6 People",
                    "Innova(7 Seater) - 7 People",
                    "Tavera(9 Seater) - 8 People",
                    "Tavera(9 Seater) - 9 People",
                    "TT (13 Seater) - 10 People",
                    "TT (13 Seater) - 11 People",
                    "TT (13 Seater) - 12 People",
                    "TT (13 Seater) - 13 People",
                    "TT (18 Seater) - 14 People",
                    "TT (18 Seater) - 15 People",
                    "TT (18 Seater) - 16 People",
                    "TT (18 Seater) - 17 People",
                    "TT (18 Seater) - 18 People",
                    "Bus (21 Seater) - 19 People",
                    "Bus (21 Seater) - 20 People",
                    "Bus (21 Seater) - 21 People",
                  ].map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Price</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={form.price}
                  onChange={(e) => set("price", Number(e.target.value))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Original Price</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={form.originalPrice}
                  onChange={(e) => set("originalPrice", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Currency</Form.Label>
                <Form.Select
                  value={form.currencyCode}
                  onChange={(e) => set("currencyCode", e.target.value)}
                >
                  {["INR", "USD", "EUR"].map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Valid From</Form.Label>
                <IsoDatePicker
                  value={form.validFrom}
                  onChange={(value) => set("validFrom", value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Valid To</Form.Label>
                <IsoDatePicker
                  value={form.validTo}
                  onChange={(value) => set("validTo", value)}
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Check
                type="switch"
                id="isActive"
                label="Active"
                checked={!!form.isActive}
                onChange={(e) => set("isActive", e.target.checked)}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
          <Button type="submit">Save</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
